import React, { useState } from "react";
import { Button, Modal } from "../../../components/ui";
import ClientPickerField from "./ClientPickerField";

/** Who was picked — enough to open a builder on and to name in a toast. */
export interface ChosenClient {
  id: string;
  name: string;
}

export interface ChooseClientModalProps {
  /** "Create supplement" — what the pick is the first step of. */
  title: string;
  /** "Choose client for supplement" — what the field asks. */
  label: string;
  /** What the confirming button says. "Next" when a second step follows. */
  confirmLabel?: string;
  onClose: () => void;
  onChoose: (client: ChosenClient) => void;
}

/**
 * The one-question dialog: who is this for?
 *
 * What the Create plan screen puts in front of the supplement builder and the
 * exercise dialog, both of which were written for a client's own screen and
 * take the client as read. The meal plan's dialog carries the same question as
 * its own first step — see `CreatePlanModal` — so this is only ever for the
 * two whose next step is a screen or a dialog of their own.
 *
 * Mounted only while it is open, so the roster is fetched when the question
 * is asked rather than with the screen behind it.
 */
const ChooseClientModal: React.FC<ChooseClientModalProps> = ({
  title,
  label,
  confirmLabel = "Next",
  onClose,
  onChoose,
}) => {
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      className="max-w-[526px]"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="md"
            disabled={!clientId}
            onClick={() => onChoose({ id: clientId, name: clientName })}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <ClientPickerField
        id="choose-client"
        label={label}
        clientId={clientId}
        onChange={(id, name) => {
          setClientId(id);
          setClientName(name);
        }}
      />
    </Modal>
  );
};

export default ChooseClientModal;
